import React, { useState } from 'react';
import { Search, Bell, ChevronDown, Menu, Command } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Link } from 'react-router-dom';
import NotificationCenter from './NotificationCenter';
import ModalManager from './ModalManager';
import { cn } from '../lib/utils';
import { GlobalSearch } from './common/GlobalSearch';

const API = process.env.REACT_APP_BACKEND_URL;

const DashboardHeader = ({ onMenuClick, sidebarCollapsed }) => {
  const { user, logout } = useAuth();
  const [activeModal, setActiveModal] = useState(null);
  const [modalData, setModalData] = useState(null);
  const [loadingOperation, setLoadingOperation] = useState(false);

  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  const getProfileLink = () => {
    const role = user?.role || '';
    if (role === 'chauffeur' || role === 'driver') return '/chauffeur/profil';
    if (role === 'expediteur' || role === 'client') return '/mon-compte';
    return '/admin/mon-profil';
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.actionType || !notification.operationId) return;
    setLoadingOperation(true);
    try {
      const response = await axios.get(
        `${API}/transport-requests/${notification.operationId}`,
        { headers: { 'Authorization': 'Bearer cookie-auth' } }
      );
      setModalData(response.data);
      setActiveModal(notification.actionType);
    } catch (error) {
      console.error('Error fetching operation:', error);
    } finally {
      setLoadingOperation(false);
    }
  };

  const handleCloseModal = () => { setActiveModal(null); setModalData(null); };
  const handleModalSuccess = () => { handleCloseModal(); };

  return (
    <header className={cn(
      "bg-white border-b border-gray-200 px-3 sm:px-6 py-3 sm:py-4 fixed top-0 right-0 z-40 shadow-sm transition-all duration-300",
      // Desktop: offset by sidebar width
      sidebarCollapsed ? "lg:left-16" : "lg:left-64",
      // Mobile: full width
      "left-0"
    )}>
      <div className="flex items-center justify-between gap-2 sm:gap-4">
        {/* Mobile hamburger */}
        <button
          onClick={onMenuClick}
          data-testid="mobile-menu-btn"
          className="lg:hidden p-2 -ml-1 text-gray-600 hover:bg-gray-100 rounded-lg"
        >
          <Menu className="w-5 h-5" />
        </button>

        {/* Search trigger - Cmd+K */}
        <div className="hidden sm:block flex-1 max-w-xl">
          <button
            onClick={() => document.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true }))}
            className="flex items-center gap-2 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-400 hover:border-gray-300 hover:bg-gray-50 transition-colors"
            data-testid="global-search-trigger"
          >
            <Search className="w-4 h-4" />
            <span className="flex-1 text-left">Rechercher...</span>
            <kbd className="hidden md:flex items-center gap-0.5 px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded text-xs font-mono border border-gray-200">
              <Command className="w-3 h-3" />K
            </kbd>
          </button>
        </div>

        {/* Spacer on mobile */}
        <div className="flex-1 sm:hidden" />

        {/* Right Section */}
        <div className="flex items-center gap-2 sm:gap-4">
          <NotificationCenter 
            userId={user?.id} 
            onNotificationClick={handleNotificationClick}
          />

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger className="flex items-center gap-1 sm:gap-3 hover:bg-gray-100 rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 transition-colors">
              <Avatar className="h-8 w-8 sm:h-9 sm:w-9 border-2 border-[#1db584]">
                <AvatarFallback className="bg-gradient-to-br from-[#1db584] to-[#16a075] text-white font-semibold text-xs sm:text-sm">
                  {getInitials(user?.firstName, user?.lastName)}
                </AvatarFallback>
              </Avatar>
              {/* Name hidden on mobile */}
              <div className="text-left hidden md:block">
                <p className="text-sm font-semibold text-gray-900 truncate max-w-[120px]">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="text-xs text-gray-500 truncate max-w-[120px]">{user?.email}</p>
              </div>
              <ChevronDown className="w-4 h-4 text-gray-500 hidden sm:block" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>Mon compte</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to={getProfileLink()} className="cursor-pointer" data-testid="header-profile-link">Profil</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to={getProfileLink()} className="cursor-pointer">Paramètres</Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} className="text-red-600 cursor-pointer">Déconnexion</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      
      <ModalManager
        activeModal={activeModal}
        modalData={modalData}
        onClose={handleCloseModal}
        onSuccess={handleModalSuccess}
      />
      <GlobalSearch />
    </header>
  );
};

export default DashboardHeader;
